import { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Platform, StyleSheet, View } from 'react-native';
import { colors } from '../../../theme/colors';

interface AgentVoiceVisualizerProps {
  isActive: boolean;
  isAgentSpeaking: boolean;
  reducedMotion?: boolean;
}

function useSystemReducedMotion(override?: boolean): boolean {
  const [reduced, setReduced] = useState(override ?? false);

  useEffect(() => {
    if (override !== undefined) {
      setReduced(override);
      return;
    }

    let cancelled = false;

    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReduced(mq.matches);
      const listener = (e: MediaQueryListEvent) => {
        if (!cancelled) setReduced(e.matches);
      };
      mq.addEventListener('change', listener);
      return () => {
        cancelled = true;
        mq.removeEventListener('change', listener);
      };
    }

    AccessibilityInfo.isReduceMotionEnabled().then((value) => {
      if (!cancelled) setReduced(value);
    });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (value) => {
      if (!cancelled) setReduced(value);
    });
    return () => {
      cancelled = true;
      sub.remove();
    };
  }, [override]);

  return reduced;
}

export function AgentVoiceVisualizer({
  isActive,
  isAgentSpeaking,
  reducedMotion: reducedMotionOverride,
}: AgentVoiceVisualizerProps) {
  const reducedMotion = useSystemReducedMotion(reducedMotionOverride);
  const outerScale = useRef(new Animated.Value(1)).current;
  const innerScale = useRef(new Animated.Value(1)).current;
  const outerOpacity = useRef(new Animated.Value(0.3)).current;
  const innerOpacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    if (reducedMotion) {
      Animated.timing(outerOpacity, {
        toValue: isAgentSpeaking ? 0.85 : 0.3,
        duration: 200,
        useNativeDriver: true,
      }).start();
      return;
    }

    if (isAgentSpeaking) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.parallel([
            Animated.timing(outerScale, { toValue: 1.18, duration: 600, useNativeDriver: true }),
            Animated.timing(outerOpacity, { toValue: 0.85, duration: 600, useNativeDriver: true }),
          ]),
          Animated.parallel([
            Animated.timing(outerScale, { toValue: 1, duration: 600, useNativeDriver: true }),
            Animated.timing(outerOpacity, { toValue: 0.4, duration: 600, useNativeDriver: true }),
          ]),
        ]),
      );
      pulse.start();
      return () => pulse.stop();
    } else {
      Animated.parallel([
        Animated.timing(outerScale, { toValue: 1, duration: 300, useNativeDriver: true }),
        Animated.timing(outerOpacity, { toValue: 0.3, duration: 300, useNativeDriver: true }),
      ]).start();
    }
  }, [isAgentSpeaking, reducedMotion, outerScale, outerOpacity]);

  useEffect(() => {
    if (reducedMotion) {
      Animated.timing(innerOpacity, {
        toValue: isActive ? 0.85 : 0.3,
        duration: 200,
        useNativeDriver: true,
      }).start();
      return;
    }

    if (isActive) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.parallel([
            Animated.timing(innerScale, { toValue: 1.12, duration: 400, useNativeDriver: true }),
            Animated.timing(innerOpacity, { toValue: 0.9, duration: 400, useNativeDriver: true }),
          ]),
          Animated.parallel([
            Animated.timing(innerScale, { toValue: 1, duration: 400, useNativeDriver: true }),
            Animated.timing(innerOpacity, { toValue: 0.5, duration: 400, useNativeDriver: true }),
          ]),
        ]),
      );
      pulse.start();
      return () => pulse.stop();
    } else {
      Animated.parallel([
        Animated.timing(innerScale, { toValue: 1, duration: 300, useNativeDriver: true }),
        Animated.timing(innerOpacity, { toValue: 0.3, duration: 300, useNativeDriver: true }),
      ]).start();
    }
  }, [isActive, reducedMotion, innerScale, innerOpacity]);

  return (
    <View style={styles.container} aria-hidden accessible={false}>
      <Animated.View
        style={[styles.outerRing, { transform: [{ scale: outerScale }], opacity: outerOpacity }]}
      />
      <Animated.View
        style={[styles.innerRing, { transform: [{ scale: innerScale }], opacity: innerOpacity }]}
      />
      <View style={styles.core} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 120,
    height: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  outerRing: {
    position: 'absolute',
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 2,
    borderColor: colors.primary.main,
    backgroundColor: colors.primary.muted,
  },
  innerRing: {
    position: 'absolute',
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 2,
    borderColor: colors.primary[300],
    backgroundColor: colors.primary.subtle,
  },
  core: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary[400],
  },
});
